"use client"

import { useState } from "react"
import {
  Plus,
  Search,
  Pin,
  Star,
  MoreHorizontal,
  Trash2,
  PinOff,
  StarOff,
  MessageSquare,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type { Conversation } from "@/lib/ai-chat/types"
import { useTranslation } from "@/lib/i18n"

interface ChatSidebarProps {
  conversations: Conversation[]
  activeId: string | null
  onSelect: (id: string) => void
  onNewChat: () => void
  onDelete: (id: string) => void
  onTogglePin: (id: string) => void
  onToggleStar: (id: string) => void
  onSearch: (query: string) => void
  disabled?: boolean
}

function relativeTime(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return "just now"
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return new Date(dateStr).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

export function ChatSidebar({
  conversations,
  activeId,
  onSelect,
  onNewChat,
  onDelete,
  onTogglePin,
  onToggleStar,
  onSearch,
  disabled = false,
}: ChatSidebarProps) {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")

  const handleSearch = (q: string) => {
    setSearchQuery(q)
    onSearch(q)
  }

  const pinned = conversations.filter((c) => c.isPinned)
  const recent = conversations.filter((c) => !c.isPinned)

  return (
    <TooltipProvider delayDuration={300}>
    {/* Fills the panel that holds it. A fixed 280 px was wider than both
        hosts' columns (clamp 220/200 px minimum), which clip: "New Chat" and
        the search box were cut off at the divider (browser QA, 2026-10-06),
        and closing a row's menu scrolled the list's start edge out of view
        (#832). The floor, no wider than the narrowest column, keeps the
        contents whole while the column animates shut. */}
    <div className="flex flex-col h-full w-full min-w-[200px] bg-sidebar">
      {/* New Chat Button */}
      <div className="p-3">
        <Button onClick={onNewChat} className="w-full gap-2" size="sm" disabled={disabled}>
          <Plus className="h-4 w-4" />
          New Chat
        </Button>
      </div>

      {/* Search */}
      <div className="px-3 pb-2">
        <div className="relative">
          <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search chats..."
            className="h-8 ps-8 text-xs"
            disabled={disabled}
          />
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto px-2">
        {/* Pinned Section */}
        {pinned.length > 0 && (
          <div className="mb-2">
            <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
              Pinned
            </p>
            {pinned.map((conv) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isActive={conv.id === activeId}
                onSelect={() => onSelect(conv.id)}
                onDelete={() => onDelete(conv.id)}
                onTogglePin={() => onTogglePin(conv.id)}
                onToggleStar={() => onToggleStar(conv.id)}
                disabled={disabled}
              />
            ))}
          </div>
        )}

        {/* Recent Section */}
        {recent.length > 0 && (
          <div>
            {pinned.length > 0 && (
              <p className="px-2 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                Recent
              </p>
            )}
            {recent.map((conv) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isActive={conv.id === activeId}
                onSelect={() => onSelect(conv.id)}
                onDelete={() => onDelete(conv.id)}
                onTogglePin={() => onTogglePin(conv.id)}
                onToggleStar={() => onToggleStar(conv.id)}
                disabled={disabled}
              />
            ))}
          </div>
        )}

        {conversations.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <MessageSquare className="h-8 w-8 mb-2 opacity-30" />
            <p className="text-xs">{t('empty.noConversations')}</p>
          </div>
        )}
      </div>
    </div>
    </TooltipProvider>
  )
}

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onTogglePin,
  onToggleStar,
  disabled,
}: {
  conversation: Conversation
  isActive: boolean
  onSelect: () => void
  onDelete: () => void
  onTogglePin: () => void
  onToggleStar: () => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  return (
    <Tooltip>
      <TooltipTrigger asChild>
    <div
      className={cn(
        "group flex items-center gap-2 px-2 py-2 rounded-lg transition-colors mb-0.5",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/50 text-foreground"
      )}
      onClick={disabled ? undefined : onSelect}
      aria-disabled={disabled}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium truncate">{conversation.title}</p>
          {conversation.isStarred && (
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 shrink-0" />
          )}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {relativeTime(conversation.updatedAt)}
          {conversation.messageCount > 0 && ` · ${t('aiChat.messageCount', { count: conversation.messageCount })}`}
        </p>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
            onClick={(e) => e.stopPropagation()}
            disabled={disabled}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          <DropdownMenuItem disabled={disabled} onClick={(e) => { e.stopPropagation(); onTogglePin() }}>
            {conversation.isPinned ? (
              <><PinOff className="h-3.5 w-3.5 me-2" />Unpin</>
            ) : (
              <><Pin className="h-3.5 w-3.5 me-2" />Pin</>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem disabled={disabled} onClick={(e) => { e.stopPropagation(); onToggleStar() }}>
            {conversation.isStarred ? (
              <><StarOff className="h-3.5 w-3.5 me-2" />Unstar</>
            ) : (
              <><Star className="h-3.5 w-3.5 me-2" />Star</>
            )}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => { e.stopPropagation(); onDelete() }}
            disabled={disabled}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5 me-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="max-w-[300px]">
        <p className="text-xs">{conversation.title}</p>
      </TooltipContent>
    </Tooltip>
  )
}
